import React, {useContext, useEffect, useState,} from "react";
import "./overrides/react-image-gallery-thumbnails";
import {PageLayout} from "./layout/PageLayout";
import {TabsContext} from "./contexts/tabsContext";
import {dialogOpenContext} from "./contexts/dialogContext";
import {MapContextProviderBuildingArea} from "./contexts/mapContext";
import {TabPanel} from "./layout/TabPanel";
import {LoadingComponent} from "./layout/LoadingComponent";
import {Dialog, Theme, useMediaQuery} from "@mui/material";
import {headers} from "./services/utils";
import {useTranslation} from "react-i18next";
import {Mixpanel} from "./analytics/Mixpanel";
import {MetricMessage} from "./analytics/metrics";
import {useFetchData} from "./hooks/useFetchData";
import PropertyWrapper from "./pages/property/PropertyWrapper";
import {BuildingAreaPage} from "./pages/building-area/BuildingAreaPage";
import {facilityType} from "./constants/constants";
import {getPropertiesOfTabGroup} from "./constants/propertyTabs";
import {buildTabs, propertyTabKey, TabKey} from "./constants/tabs";
import OpenWidgetButton from "./layout/OpenWidgetButton";
import { WidgetSettings } from "./models/WidgetSettings";
import {DialogStylesWrapper} from "./styles/Wrappers";
import IFramer from "./layout/IFramer";
import CookieBanner from "./layout/CookieBanner";
import {useStorageData} from "./hooks/useStorageData";
import {writeStorage} from "./utils/storage";
import {getAccessibilityUnitLevelNumber} from "./utils/accessibilities";
import {getTextByCurrentLanguage} from "./utils/language";
import FeedbackModal from "./layout/feedback-modal/FeedbackModal";
import {feedbackOpenContext} from "./contexts/feedbackContext";
import List from "./components/list/List";
import Item from "./components/list/components/Item";

function App(props: {
  openDefault: boolean
  companyIdAPIKey: string | null,
  widgetAccessibilityButtonSettings: WidgetSettings[] | undefined,
  buildingAreaId: string | null,
  propertyId: string | null
}) {
  const { companyIdAPIKey, widgetAccessibilityButtonSettings, buildingAreaId, propertyId } = props;
  const [addedMetricsGroup, setAddedMetricsGroup] = useState(false);
  const [buttonLoaded, setButtonLoaded] = useState<boolean>(false);
  const id = buildingAreaId ? buildingAreaId : propertyId;
  const type = buildingAreaId ? facilityType.buildingArea : facilityType.property;
  const { t, i18n } = useTranslation();
  const [dialogOpen, setDialogOpen] = useContext(dialogOpenContext);
  const [feedbackOpen, setFeedbackOpen] = useContext(feedbackOpenContext);
  const sm = useMediaQuery((theme: Theme) => theme.breakpoints.down("sm"));
  const cookieConsent = useStorageData('cookie-consent')
  const isInIframe = window.self !== window.top
  const feedbackShown = useStorageData('feedbackShown')
  const [feedbackTimeout, setFeedbackTimeout] = useState(false)
  const [, setField] = useContext(TabsContext);

  // accept building area id and property id
  const {
    properties,
    a11yStandard,
    company,
    loading,
    selectedBuildingArea,
    shopsCount,
    parkingSpaces,
    publicToilets,
    toilets,
    buildingAreaAccessibilityInfo,
    published,
    accessibilityUnits,
    accessibilities,
    questions
  } = useFetchData(id, type);

  useEffect(() => {
    if (company) {
      if (i18n.language && company.supported_languages.includes(i18n.language)) {
        headers["Accept-Language"] = i18n.language
      } else {
        i18n.changeLanguage(company.supported_languages[0], (err) => {
          if (err) return console.log('something went wrong loading language', err);
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [i18n.language, company]);

  useEffect(() => {
    // adding mixpanel group in different cases
    const inFullscreenMode =
      JSON.parse(cookieConsent) && props.openDefault && company ||
      props.openDefault && company && isInIframe
    const isNotFullscreenMode = !props.openDefault && company
    if (inFullscreenMode || isNotFullscreenMode) {
      setMixpanelGroup()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [company, cookieConsent, props.openDefault])

  const setMixpanelGroup = () => {
    if (company && Mixpanel.setGroup && !addedMetricsGroup) {
      const group: {
        companyId: string,
        propertyId?: string | null,
        buildingAreaId?: string | null
      } = {
        companyId: company.id,
      }
      group.propertyId = type === facilityType.property ? id : null
      group.buildingAreaId = type === facilityType.buildingArea ? id : null

      setAddedMetricsGroup(true);
      Mixpanel.setGroup(group);
      if (!buttonLoaded) {
        setButtonLoaded(true);
      }
    }
  }

  useEffect(() => {
    if (props.openDefault) {
      setDialogOpen(true);
    }
    writeStorage('fullscreen', props.openDefault)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // quickfix for funnys adventure widget
    if (dialogOpen) {
      document.body.style.overflow = "hidden"
      let footers = document.getElementsByTagName('footer');
      if (footers.length > 0) {
        footers[0].style.zIndex = '9999'
      }
    } else {
      document.body.style.overflow = "auto"
    }
  }, [dialogOpen])

  useEffect(() => {
    const isNotWrappedInIframe = props.openDefault && company && dialogOpen && JSON.parse(cookieConsent)
    const isWrappedInIframe = props.openDefault && company && dialogOpen && isInIframe
    if (isNotWrappedInIframe || isWrappedInIframe) {
      Mixpanel.track(`${MetricMessage.accessed_widget_button}`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dialogOpen, props.openDefault, company, cookieConsent]);

  useEffect(() => {
    if (!feedbackShown) {
      let feedbackTimeoutLink = setTimeout(() => {
        setFeedbackTimeout(true)
      }, 40000)
      return () => {
        clearTimeout(feedbackTimeoutLink);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const hasData = properties ? properties.length > 0 && !!a11yStandard : false;

  const loadingState = properties ? (properties.length <= 0) : true;

  const tabs = buildTabs(selectedBuildingArea, properties ?? []);

  // the header of a property tab is the property, of the other tabs the building area
  const headerTexts = tabs.reduce<Record<TabKey, string>>((texts, tab) => ({
    ...texts,
    [tab.key]: tab.kind === 'property' ? getTextByCurrentLanguage(tab.property.name) : selectedBuildingArea?.name ?? '',
  }), {});

  const onAccessibilityClick = () => {
    if (company) {
      Mixpanel.track(`${MetricMessage.accessed_widget_button}`);
    }
    return setDialogOpen(!dialogOpen);
  };

  const isDisplayShops = () => (
    !!(widgetAccessibilityButtonSettings
      && widgetAccessibilityButtonSettings[0]
      && widgetAccessibilityButtonSettings[0].is_display_shops)
  )


  // Check if document in iframe
  if (window.self !== window.top) {
    const metaViewport = document.querySelector('meta[name="viewport"]');
    metaViewport?.setAttribute('content', 'width=device-width, initial-scale=0.9');
  }

  const handleCloseFeedback = () => {
    Mixpanel.track(`${MetricMessage.closed_widget_window}`);
    if (props.openDefault) {
      window.close()
    } else {
      setDialogOpen(false);
    }
    setFeedbackOpen(false)
  }

  const handleDialogClose = (e: any) => {
    if (feedbackShown || !feedbackTimeout) {
      if (company) {
        Mixpanel.track(`${MetricMessage.closed_widget_modal} `);
      }
      setDialogOpen(false);
    } else if (feedbackTimeout) {
      Mixpanel.track(`${MetricMessage.feedback_shown}`)
      writeStorage("feedbackShown", true)
      e.preventDefault()
      e.stopPropagation()
      setFeedbackOpen(true)
    }
  }

  const openProperty = (propertyId: string) => setField(propertyTabKey(propertyId));

  return (
    <>
      {companyIdAPIKey ?
        <div className="handicover-widget-entrypoint">
          {!props.openDefault ? (
            <OpenWidgetButton
              companyIdAPIKey={companyIdAPIKey}
              a11yStandard={a11yStandard}
              settings={widgetAccessibilityButtonSettings}
              loadingState={loadingState}
              loading={loading}
              t={t}
              onAccessibilityClick={onAccessibilityClick}
            />
          ) : null
          }
          <IFramer
            component={
              <DialogStylesWrapper>
                <Dialog
                  disablePortal
                  open={dialogOpen}
                  fullScreen={props.openDefault ? true : sm}
                  onClose={handleDialogClose}
                  className="handiscover-accessibility-widget"
                  fullWidth
                  maxWidth="lg"
                >
                  {properties && a11yStandard && company ? (
                    <PageLayout
                      fullScreen={props.openDefault}
                      loading={loading}
                      properties={properties}
                      tabs={tabs}
                      currentStandard={a11yStandard}
                      headerTexts={headerTexts}
                      companyLogo={company?.logo}
                      propertyName={company?.name ?? ""}
                      buildingArea={selectedBuildingArea}
                      companyName={company?.name}
                      supportedLanguages={company?.supported_languages}
                    >
                      {loading || !a11yStandard ? (
                        <LoadingComponent />
                      ) : hasData ? (
                        <>
                          {tabs.map((tab) => {
                            if (tab.kind === 'buildingArea') {
                              return selectedBuildingArea && buildingAreaId ? (
                                <TabPanel key={tab.key} value={tab.key} fullscreen={props.openDefault}>
                                  <MapContextProviderBuildingArea>
                                    <BuildingAreaPage
                                      buildingArea={selectedBuildingArea}
                                      a11yStandard={a11yStandard}
                                      propertiesCount={properties.length}
                                      shopsCount={shopsCount}
                                      parkingSpaces={getAccessibilityUnitLevelNumber(parkingSpaces, accessibilityUnits)}
                                      publicToilets={getAccessibilityUnitLevelNumber(publicToilets, accessibilityUnits)}
                                      toilets={getAccessibilityUnitLevelNumber(toilets, accessibilityUnits)}
                                      properties={properties}
                                      buildingAreaAccessibilityInfo={buildingAreaAccessibilityInfo}
                                      buildingAreaId={buildingAreaId}
                                      fullscreen={props.openDefault}
                                      accessibilityUnits={accessibilityUnits}
                                      accessibilities={accessibilities}
                                      questions={questions}
                                    />
                                  </MapContextProviderBuildingArea>
                                </TabPanel>
                              ) : null;
                            }
                            if (tab.kind === 'group') {
                              return (
                                <TabPanel key={tab.key} value={tab.key} fullscreen={props.openDefault}>
                                  <List items={getPropertiesOfTabGroup(properties, tab.group).map((p) => (
                                    <Item key={p.id} onClick={openProperty} {...p}/>
                                  ))}/>
                                </TabPanel>
                              );
                            }
                            return (
                              <TabPanel key={tab.key} value={tab.key} fullscreen={props.openDefault}>
                                <PropertyWrapper
                                  property={tab.property}
                                  a11yStandard={a11yStandard}
                                  displayShops={isDisplayShops()}
                                  companyIdAPIKey={companyIdAPIKey}
                                  buildingAreaId={buildingAreaId}
                                  fullscreen={props.openDefault}
                                />
                              </TabPanel>
                            );
                          })}
                        </>
                      ) : null}
                      <FeedbackModal
                        onClose={handleCloseFeedback}
                        open={feedbackOpen}
                      />
                    </PageLayout>
                  ) : (published) ? (
                    <LoadingComponent />
                  ) : null}
                </Dialog>
                <CookieBanner display={props.openDefault && !isInIframe}/>
              </DialogStylesWrapper>
            }
            dialogOpen={dialogOpen ? 1 : 0}
            name={'Handiscover Dialog'}
          />
        </div> : <></>
      }
    </>
  );
}

export default App;
